import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Package, Truck, Store, Receipt, ArrowRight, CheckCircle2,
  TrendingUp, AlertCircle, Wallet, ChevronRight,
  Calendar, FilePlus, ClipboardList,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { cn } from "@/lib/utils";
import { API } from "@/lib/api";

const fmt = (amount) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency", currency: "INR",
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(amount);

const PIPELINE_CONFIG = [
  { key: "New",        icon: Package,      color: "text-slate-600",  dot: "bg-slate-400",   bg: "bg-slate-50"   },
  { key: "Pickup",     icon: Truck,        color: "text-blue-600",   dot: "bg-blue-400",    bg: "bg-blue-50"    },
  { key: "At Merchant",icon: Store,        color: "text-amber-600",  dot: "bg-amber-400",   bg: "bg-amber-50"   },
  { key: "Billing",    icon: Receipt,      color: "text-orange-600", dot: "bg-orange-400",  bg: "bg-orange-50"  },
  { key: "Delivery",   icon: ArrowRight,   color: "text-cyan-600",   dot: "bg-cyan-400",    bg: "bg-cyan-50"    },
  { key: "Closed",     icon: CheckCircle2, color: "text-emerald-600",dot: "bg-emerald-400", bg: "bg-emerald-50" },
];

export const Dashboard = () => {
  const [kanban, setKanban]           = useState({});
  const [pendingPayouts, setPendingPayouts] = useState([]);
  const [gmvStats, setGmvStats]       = useState({ total_invoices: 0, total_gmv: 0 });
  const [chartData, setChartData]     = useState([]);
  const [dateRange, setDateRange]     = useState({ from: null, to: null });
  const [loading, setLoading]         = useState(true);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateRange.from) params.append("start_date", format(dateRange.from, "yyyy-MM-dd"));
      if (dateRange.to)   params.append("end_date",   format(dateRange.to,   "yyyy-MM-dd"));

      const [kanbanRes, payoutRes, statsRes, chartRes] = await Promise.all([
        fetch(`${API}/orders/kanban`),
        fetch(`${API}/payee/queue?status=pending`),
        fetch(`${API}/dashboard/stats?${params}`),
        fetch(`${API}/dashboard/chart?${params}`),
      ]);

      setKanban(await kanbanRes.json());
      setPendingPayouts(await payoutRes.json());
      setGmvStats(await statsRes.json());
      const raw = await chartRes.json();
      setChartData(raw.map((d) => ({ ...d, date: format(new Date(d.date), "MMM dd") })));
    } catch (e) {
      console.error("Dashboard fetch error", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, [dateRange]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Derived metrics ──────────────────────────────────────────────
  const activeOrders = PIPELINE_CONFIG
    .filter((b) => b.key !== "Closed")
    .reduce((sum, b) => sum + (kanban[b.key]?.length || 0), 0);

  const closedOrders  = kanban["Closed"]?.length || 0;
  const newOrders     = kanban["New"]?.length     || 0;
  const billingOrders = kanban["Billing"]?.length  || 0;
  const needsAttention = newOrders + billingOrders;

  const pendingPayoutCount  = pendingPayouts.length;
  const pendingPayoutAmount = pendingPayouts.reduce(
    (sum, o) => sum + (o.invoice_amount || 0), 0
  );

  const clearDateRange = () => setDateRange({ from: null, to: null });

  return (
    <div className="space-y-7" data-testid="dashboard-page">

      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Dashboard</h1>
          <p className="text-slate-500 mt-1 text-sm">Pokus Operations Overview</p>
        </div>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn("justify-start text-left font-normal text-sm", !dateRange.from && "text-muted-foreground")}
            >
              <Calendar className="mr-2 h-4 w-4" />
              {dateRange.from
                ? (dateRange.to
                    ? `${format(dateRange.from, "MMM dd")} – ${format(dateRange.to, "MMM dd, yyyy")}`
                    : format(dateRange.from, "MMM dd, yyyy"))
                : "All Time"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <CalendarComponent
              initialFocus mode="range"
              defaultMonth={dateRange.from}
              selected={dateRange}
              onSelect={setDateRange}
              numberOfMonths={2}
            />
            <div className="p-3 border-t">
              <Button variant="ghost" size="sm" className="w-full" onClick={clearDateRange}>
                Clear (Show All Time)
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {/* ── Top KPIs ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Active Orders */}
        <Card className="border-slate-200">
          <CardContent className="p-5">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Active Orders</p>
            <p className="text-3xl font-bold text-slate-900 font-mono">{loading ? "–" : activeOrders}</p>
            <p className="text-xs text-slate-400 mt-1">in pipeline right now</p>
          </CardContent>
        </Card>

        {/* GMV */}
        <Card className="border-slate-200">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">GMV</p>
                <p className="text-3xl font-bold text-slate-900 font-mono">{loading ? "–" : fmt(gmvStats.total_gmv)}</p>
                <p className="text-xs text-slate-400 mt-1">{gmvStats.total_invoices} invoice{gmvStats.total_invoices !== 1 ? "s" : ""}</p>
              </div>
              <div className="p-2 rounded-lg bg-emerald-50 mt-1">
                <TrendingUp className="w-4 h-4 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pending Payouts */}
        <Card className={cn("border-slate-200", pendingPayoutCount > 0 && "border-orange-200 bg-orange-50/40")}>
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Payouts Pending</p>
                <p className={cn("text-3xl font-bold font-mono", pendingPayoutCount > 0 ? "text-orange-600" : "text-slate-900")}>
                  {loading ? "–" : pendingPayoutCount}
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  {pendingPayoutAmount > 0 ? fmt(pendingPayoutAmount) + " to disburse" : "nothing pending"}
                </p>
              </div>
              <div className={cn("p-2 rounded-lg mt-1", pendingPayoutCount > 0 ? "bg-orange-100" : "bg-slate-100")}>
                <Wallet className={cn("w-4 h-4", pendingPayoutCount > 0 ? "text-orange-600" : "text-slate-400")} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Orders Closed */}
        <Card className="border-slate-200">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Closed Orders</p>
                <p className="text-3xl font-bold text-slate-900 font-mono">{loading ? "–" : closedOrders}</p>
                <p className="text-xs text-slate-400 mt-1">completed end-to-end</p>
              </div>
              <div className="p-2 rounded-lg bg-emerald-50 mt-1">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ── Pipeline Strip ── */}
      <Link to="/orders">
        <Card className="border-slate-200 hover:border-[#0047FF]/30 hover:shadow-sm transition-all cursor-pointer">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">Order Pipeline</p>
              <span className="text-xs text-[#0047FF] font-medium flex items-center gap-0.5">
                View all <ChevronRight className="w-3 h-3" />
              </span>
            </div>
            <div className="grid grid-cols-6 gap-2">
              {PIPELINE_CONFIG.map(({ key, icon: Icon, color, dot, bg }) => {
                const count = kanban[key]?.length || 0;
                return (
                  <div key={key} className={cn("rounded-lg p-3 text-center", bg)}>
                    <Icon className={cn("w-4 h-4 mx-auto mb-1.5", color)} />
                    <p className={cn("text-lg font-bold font-mono leading-none", color)}>{count}</p>
                    <p className="text-[10px] text-slate-500 mt-1 leading-tight hidden sm:block">{key}</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </Link>

      {/* ── Attention + Payout row ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

        {/* Needs Attention */}
        <Card className={cn("border-slate-200", needsAttention > 0 && "border-amber-200")}>
          <CardHeader className="pb-2 pt-4 px-5">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <AlertCircle className={cn("w-4 h-4", needsAttention > 0 ? "text-amber-500" : "text-slate-300")} />
                Needs Attention
              </CardTitle>
              <Link to="/orders">
                <Button variant="ghost" size="sm" className="h-7 text-xs text-slate-500">Open orders →</Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent className="px-5 pb-4 space-y-3">
            {needsAttention === 0 ? (
              <p className="text-sm text-slate-400 py-2">All clear — no orders need immediate action.</p>
            ) : (
              <>
                {newOrders > 0 && (
                  <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-200">
                    <div className="flex items-center gap-2.5">
                      <Package className="w-4 h-4 text-slate-500" />
                      <div>
                        <p className="text-sm font-medium text-slate-800">New — awaiting rider</p>
                        <p className="text-xs text-slate-500">Need pickup rider assigned</p>
                      </div>
                    </div>
                    <span className="text-lg font-bold font-mono text-slate-700">{newOrders}</span>
                  </div>
                )}
                {billingOrders > 0 && (
                  <div className="flex items-center justify-between p-3 rounded-lg bg-orange-50 border border-orange-200">
                    <div className="flex items-center gap-2.5">
                      <Receipt className="w-4 h-4 text-orange-500" />
                      <div>
                        <p className="text-sm font-medium text-slate-800">Billing — awaiting action</p>
                        <p className="text-xs text-slate-500">Invoice / payout step pending</p>
                      </div>
                    </div>
                    <span className="text-lg font-bold font-mono text-orange-600">{billingOrders}</span>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Payout Queue */}
        <Card className={cn("border-slate-200", pendingPayoutCount > 0 && "border-orange-200")}>
          <CardHeader className="pb-2 pt-4 px-5">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <Wallet className={cn("w-4 h-4", pendingPayoutCount > 0 ? "text-orange-500" : "text-slate-300")} />
                Payout Queue
              </CardTitle>
              <Link to="/payee">
                <Button variant="ghost" size="sm" className="h-7 text-xs text-slate-500">View queue →</Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent className="px-5 pb-4">
            {pendingPayoutCount === 0 ? (
              <p className="text-sm text-slate-400 py-2">No pending payouts — all clear.</p>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 rounded-lg bg-orange-50 border border-orange-200">
                  <div>
                    <p className="text-sm font-medium text-slate-800">{pendingPayoutCount} order{pendingPayoutCount !== 1 ? "s" : ""} pending</p>
                    <p className="text-xs text-slate-500">Vendor invoices awaiting payout</p>
                  </div>
                  <span className="text-lg font-bold font-mono text-orange-600">
                    {fmt(pendingPayoutAmount)}
                  </span>
                </div>
                <Link to="/payee">
                  <Button size="sm" className="w-full bg-orange-600 hover:bg-orange-700 text-white">
                    Process Payouts
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

      </div>

      {/* ── Revenue Chart ── */}
      <Card className="border-slate-200">
        <CardHeader className="pb-2 pt-4 px-5">
          <CardTitle className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Revenue Over Time</CardTitle>
        </CardHeader>
        <CardContent className="px-5 pb-5">
          {chartData.length > 0 ? (
            <div className="h-[200px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="gmvGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor="#0047FF" stopOpacity={0.12} />
                      <stop offset="95%" stopColor="#0047FF" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={{ stroke: "#e2e8f0" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={{ stroke: "#e2e8f0" }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                  <Tooltip
                    contentStyle={{ backgroundColor: "white", border: "1px solid #e2e8f0", borderRadius: "8px", fontSize: "12px" }}
                    formatter={(v, n) => [n === "amount" ? fmt(v) : v, n === "amount" ? "Revenue" : "Invoices"]}
                  />
                  <Area type="monotone" dataKey="amount" stroke="#0047FF" strokeWidth={2} fill="url(#gmvGrad)" fillOpacity={1} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-[200px] flex flex-col items-center justify-center gap-2">
              <TrendingUp className="w-8 h-8 text-slate-200" />
              <p className="text-sm text-slate-400">No revenue data for selected period</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Quick Actions ── */}
      <div>
        <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-3">Quick Actions</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { to: "/orders",       icon: ClipboardList, label: "Active Orders",       desc: "View pipeline",        color: "text-[#0047FF]", bg: "bg-blue-50",    border: "hover:border-[#0047FF]/30" },
            { to: "/orders",       icon: Package,       label: "New Order",           desc: "Create from orders",   color: "text-slate-600", bg: "bg-slate-50",   border: "hover:border-slate-300"    },
            { to: "/vendor-invoice", icon: FilePlus,    label: "Create Invoice",      desc: "Customer + vendor",    color: "text-emerald-600", bg: "bg-emerald-50", border: "hover:border-emerald-300" },
            { to: "/payee",        icon: Wallet,        label: "Payee Queue",         desc: "Process payouts",      color: "text-orange-600", bg: "bg-orange-50",  border: "hover:border-orange-300"   },
          ].map((item) => (
            <Link to={item.to} key={item.label}>
              <Card className={cn("border-slate-200 transition-all hover:shadow-sm cursor-pointer group", item.border)}>
                <CardContent className="p-4 flex items-center gap-3">
                  <div className={cn("p-2 rounded-lg shrink-0", item.bg)}>
                    <item.icon className={cn("w-4 h-4", item.color)} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{item.label}</p>
                    <p className="text-xs text-slate-400 truncate">{item.desc}</p>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>

    </div>
  );
};
